import { For, Show, type Component, type JSX } from "solid-js";
import "./donut.css";

/** Un segment du donut : valeur + couleur + libellé optionnel. */
export interface DonutSegment {
  label?: string;
  value: number;
  color: string;
}

export interface DonutProps {
  /** Segments à représenter. Les valeurs nulles ou négatives sont ignorées. */
  segments: DonutSegment[];
  /** Diamètre du SVG en pixels (défaut 84). */
  size?: number;
  /** Épaisseur de l'anneau en pixels (défaut 12). */
  thickness?: number;
  /** Contenu central (ex. ratio). */
  center?: JSX.Element;
  /** Libellé sous le donut. */
  caption?: string;
}

/**
 * Donut chart minimaliste — SVG inline, aucune dépendance.
 *
 * Chaque segment est un `<circle>` unique dont le `stroke-dasharray` encode
 * la part du segment et le `stroke-dashoffset` sa position cumulée sur
 * l'anneau. Conçu pour visualiser des ratios d'allocation (RAM, disque,
 * quotas) dans les panels opérateur.
 *
 * Usage :
 * ```tsx
 * <Donut
 *   segments={[
 *     { label: "Alloué", value: 12, color: "var(--accent, #6cf)" },
 *     { label: "Libre", value: 20, color: "var(--border, #2a3140)" },
 *   ]}
 *   center={<strong>37%</strong>}
 *   caption="RAM"
 * />
 * ```
 */
export const Donut: Component<DonutProps> = (props) => {
  const size = () => props.size ?? 84;
  const thickness = () => props.thickness ?? 12;
  const radius = () => (size() - thickness()) / 2;
  const circumference = () => 2 * Math.PI * radius();

  const clean = (): DonutSegment[] => props.segments.filter((s) => Number.isFinite(s.value) && s.value > 0);

  const total = (): number => clean().reduce((acc, s) => acc + s.value, 0);

  /** Segments avec offset cumulé pré-calculé. */
  const arcs = (): Array<DonutSegment & { dash: number; offset: number }> => {
    const t = total();
    if (t <= 0) return [];
    let cumulative = 0;
    return clean().map((s) => {
      const fraction = s.value / t;
      const dash = fraction * circumference();
      const offset = -cumulative * circumference();
      cumulative += fraction;
      return { ...s, dash, offset };
    });
  };

  return (
    <div class="donut" style={{ width: `${size()}px` }}>
      <div class="donut__chart" style={{ width: `${size()}px`, height: `${size()}px` }}>
        <svg width={size()} height={size()} viewBox={`0 0 ${size()} ${size()}`} class="donut__svg" aria-hidden="true">
          {/* Piste de fond */}
          <circle
            cx={size() / 2}
            cy={size() / 2}
            r={radius()}
            fill="none"
            stroke="var(--donut-track, rgba(127,127,127,0.18))"
            stroke-width={thickness()}
          />
          <Show when={total() > 0}>
            <For each={arcs()}>
              {(arc) => (
                <circle
                  cx={size() / 2}
                  cy={size() / 2}
                  r={radius()}
                  fill="none"
                  stroke={arc.color}
                  stroke-width={thickness()}
                  stroke-dasharray={`${arc.dash} ${circumference() - arc.dash}`}
                  stroke-dashoffset={arc.offset}
                  stroke-linecap="butt"
                  transform={`rotate(-90 ${size() / 2} ${size() / 2})`}
                >
                  <Show when={arc.label}>
                    <title>{`${arc.label}: ${arc.value}`}</title>
                  </Show>
                </circle>
              )}
            </For>
          </Show>
        </svg>
        <Show when={props.center}>
          <div class="donut__center">{props.center}</div>
        </Show>
      </div>
      <Show when={props.caption}>
        <span class="donut__caption">{props.caption}</span>
      </Show>
    </div>
  );
};
